import React, { useMemo, useState } from 'react';
import { connect } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Autocomplete,
  TextField,
  Button,
  Tooltip,
  Grid,
} from '@mui/material';
import { CorporateFare } from '@mui/icons-material';
import { Page, Row } from '../util/Page';
import { createBuildingActionCreator } from '../../redux/actions/buildings';
import { AUTH_LEVELS } from '../auth/Authorized';
import {
  associateBuildingsAction,
  createDeploymentActionCreator,
  upsertDeploymentConfigAction,
} from '../../redux/actions/deployments';
import UnitsLoader from '../loaders/UnitsLoader';
import BuildingsLoader from '../loaders/BuildingsLoader';
import OrganizationsLoader from '../loaders/OrganizationsLoader';
import { capitalize } from '../../utils/strings';

import './data-request.css';

const ALPHA_NUMERIC_DASH_REGEX = /^[a-zA-Z0-9- ]+$/;

/* eslint-disable jsx-a11y/label-has-associated-control */
/**
 * A component with internal state which manages a building data request form
 */
const NewBuilding = ({
  userOrgId,
  orgsById,
  isSuper,
  buildings,
  units,
  createBuilding,
  createDeployment,
  linkDeployment,
  updateDeploymentConfig,
  navigate,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [changes, setChanges] = useState({});
  const [newBuilding, setNewBuilding] = useState();
  const [newDeployment, setNewDeployment] = useState();
  const [createComplete, setCreateComplete] = useState(false);

  const unitIdsByName = useMemo(
    // eslint-disable-next-line camelcase
    () => Object.fromEntries(units.map(({ name_long, id }) => [name_long, id])),
    [units]
  );

  const getField = (key, defaultVal = '') => {
    if (changes[key] != null) return changes[key];
    return defaultVal;
  };
  const setField = (key, value) => setChanges({ ...changes, [key]: value });
  // Autocomplete has 2 params, others have 1
  const change = (key) => (e, newValue) => setField(key, newValue ?? e.target.value);

  const defaultUnit = units.filter((u) => u.name_abbr === 'ft2')?.[0];

  const orgId = getField('org')?.id ?? userOrgId;
  const name = getField('name');
  const nameInvalid = !ALPHA_NUMERIC_DASH_REGEX.test(name);
  const hasAddress = getField('address');
  const hasTimezone = getField('timezone');
  const area = getField('area');
  const areaInvalid = area && !Number(area);
  const nameConflict = buildings.some((b) => b.orgId === orgId && b.name === name);
  const canSubmit =
    name && hasAddress && !nameConflict && !areaInvalid && !nameInvalid && hasTimezone;
  let submitError = '';
  if (nameConflict) {
    submitError = 'building name already exists';
  }
  if (nameInvalid) {
    if (submitError) submitError += '; ';
    submitError += 'building name must only contains letters, numbers, spaces, and dashes';
  }
  if (!hasTimezone) {
    if (submitError) submitError += '; ';
    submitError += 'building timezone must be set';
  }
  if (areaInvalid) {
    if (submitError) submitError += '; ';
    submitError += 'building area must be a number';
  }
  if (!name || !hasAddress) {
    if (submitError) submitError += '; ';
    submitError += 'please complete these required fields: ';
    let comma = false;
    if (!name) {
      if (comma) submitError += ', ';
      submitError += 'building name';
      comma = true;
    }
    if (!hasAddress) {
      if (comma) submitError += ', ';
      submitError += 'address';
      comma = true;
    }
  }

  function buildSite(buildingName) {
    return [orgsById[orgId].name_abbr, buildingName.replace(/[ ]/g, '-')].join('/').toLowerCase();
  }

  const containerSpacing = 6;
  const leftItemXs = 3;
  const rightItemXs = 9;
  let dialogContents;
  if (!newBuilding) {
    dialogContents = 'Preparing new building...';
  } else if (!newDeployment) {
    dialogContents = 'New building created, preparing new deployment...';
  } else if (!createComplete) {
    dialogContents = 'New building created, new deployment created, finishing setup...';
  } else {
    dialogContents = 'New building created, new deployment created, setup complete';
  }
  return (
    <>
      <UnitsLoader />
      <BuildingsLoader />
      <OrganizationsLoader />
      <Dialog open={dialogOpen} maxWidth="sm" fullWidth>
        <DialogTitle>Create Building & Deployment</DialogTitle>
        <DialogContent>
          <DialogContentText>{dialogContents}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            disabled={!createComplete}
            onClick={() => {
              navigate(
                `/deployments?expanded=["${newDeployment}"]&filterBy={"buildings"%3A["${newBuilding?.name}"]}`
              );
            }}
            color="success"
          >
            Okay
          </Button>
        </DialogActions>
      </Dialog>
      <form
        autoComplete="off"
        id="building-form"
        onSubmit={(event) => {
          event.preventDefault();
          setDialogOpen(true);
          const newBuildingDetails = {
            orgId,
            name: event.target.elements.name.value.trim(),
            address: event.target.elements.address.value.trim(),
            purpose: event.target.elements.purpose.value?.trim() || null,
            area: event.target.elements.area.value || null,
            areaUnitId: unitIdsByName[event.target.elements.unit.value],
            timezone: event.target.elements.timezone.value,
          };

          return (
            createBuilding(newBuildingDetails)
              .then((building) => {
                setNewBuilding(building);
                return createDeployment(buildSite(building.name), building.name, orgId).then(
                  (deployment) => {
                    setNewDeployment(deployment.id);
                    linkDeployment(deployment.site, building.id);
                    return updateDeploymentConfig(deployment.site, 47808);
                  }
                );
              })
              .then((result) => {
                setCreateComplete(true);
                return result;
              })
              // eslint-disable-next-line no-console
              .catch((e) => console.log(e))
          );
        }}
      >
        <Page name="new-data-request">
          <Row>
            <Grid container spacing={containerSpacing}>
              <Grid>
                We are excited to help get your project launched! Complete the form to create your
                new building and deployment.
                <br />
                <br />
                Free trials are limited to one building. To launch additional buildings, please
                contact sales@onboarddata.io with your billing information, and our team will assist
                you. Launching additional buildings without payment will result in deactivation.
              </Grid>
            </Grid>
          </Row>
          <>
            {isSuper ? (
              <Row>
                <Grid container spacing={containerSpacing}>
                  <Grid size={{ xs: leftItemXs }}>
                    <label className="required" htmlFor="org">
                      Building Org
                    </label>
                  </Grid>
                  <Grid size={{ xs: rightItemXs }}>
                    <Autocomplete
                      id="org"
                      name="org"
                      key="org"
                      options={Object.values(orgsById)}
                      // style={{ display: 'inline-flex', width: '250px' }}
                      getOptionKey={(org) => org?.id ?? -1}
                      getOptionLabel={(org) => org?.name_abbr ?? ''}
                      value={(getField('org') || orgsById[userOrgId]) ?? {}}
                      onChange={change('org')}
                      // eslint-disable-next-line react/jsx-props-no-spreading
                      renderInput={(params) => <TextField label="Select" {...params} />}
                    />
                  </Grid>
                </Grid>
              </Row>
            ) : null}
            <Row>
              <Grid container spacing={containerSpacing}>
                <Grid size={{ xs: leftItemXs }}>
                  <label className="required" htmlFor="name">
                    Building Name
                  </label>
                </Grid>
                <Grid size={{ xs: rightItemXs }}>
                  <TextField
                    id="name"
                    required
                    error={!getField('name') || nameConflict}
                    placeholder="Baker Street Lab"
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={getField('name')}
                    onChange={change('name')}
                    onKeyDown={(event) => {
                      if (!ALPHA_NUMERIC_DASH_REGEX.test(event.key)) {
                        event.preventDefault();
                      }
                    }}
                  />
                </Grid>
              </Grid>
            </Row>
            <Row>
              <Grid container spacing={containerSpacing}>
                <Grid size={{ xs: leftItemXs }}>
                  <label htmlFor="purpose">Building Purpose</label>
                </Grid>
                <Grid size={{ xs: rightItemXs }}>
                  <TextField
                    id="purpose"
                    placeholder="Office, Stadium, Hospital, Batcave"
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={getField('purpose')}
                    onChange={change('purpose')}
                  />
                </Grid>
              </Grid>
            </Row>
            <Row>
              <Grid container spacing={containerSpacing}>
                <Grid size={{ xs: leftItemXs }}>
                  <label className="required" htmlFor="address">
                    Building Address
                  </label>
                </Grid>
                <Grid size={{ xs: rightItemXs }}>
                  <TextField
                    id="address"
                    required
                    error={!getField('address')}
                    placeholder="221B Baker Street, London, England"
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={getField('address')}
                    onChange={change('address')}
                  />
                </Grid>
              </Grid>
            </Row>
            <Row>
              <Grid container spacing={containerSpacing}>
                <Grid size={{ xs: leftItemXs }}>
                  <label htmlFor="timezone">Building Timezone</label>
                </Grid>
                <Grid size={{ xs: rightItemXs }}>
                  <Autocomplete
                    id="timezone"
                    key="timezone"
                    options={Intl.supportedValuesOf('timeZone')}
                    style={{ display: 'inline-flex', width: '250px' }}
                    value={getField('timezone')}
                    onChange={change('timezone')}
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    renderInput={(params) => <TextField label="Select" {...params} />}
                  />
                </Grid>
              </Grid>
            </Row>
            <Row>
              <Grid container spacing={containerSpacing}>
                <Grid size={{ xs: leftItemXs }}>
                  <label htmlFor="area">Building Area</label>
                </Grid>
                <Grid size={{ xs: rightItemXs }}>
                  <TextField
                    id="area"
                    error={areaInvalid}
                    placeholder="250000"
                    variant="outlined"
                    size="small"
                    value={getField('area')}
                    onChange={change('area')}
                  />
                  <Autocomplete
                    id="unit"
                    name="unit"
                    key="unit"
                    options={units}
                    style={{ display: 'inline-flex', width: '250px' }}
                    getOptionKey={(unit) => unit?.id ?? -1}
                    getOptionLabel={(unit) => unit?.name_long ?? ''}
                    value={getField('unit', defaultUnit)}
                    onChange={change('unit')}
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    renderInput={(params) => <TextField label="Select" {...params} />}
                  />
                </Grid>
              </Grid>
            </Row>
          </>
          <Row>
            <Tooltip title={canSubmit ? '' : capitalize(submitError)}>
              <span>
                <Button
                  type="submit"
                  form="building-form"
                  disabled={!canSubmit}
                  variant="contained"
                  endIcon={<CorporateFare />}
                >
                  Create Building
                </Button>
              </span>
            </Tooltip>
          </Row>
        </Page>
      </form>
    </>
  );
};

function mapStateToProps(state) {
  const {
    entities: {
      buildings: { byId },
    },
    user: { orgId, userAuthLevel },
    users: { orgsById },
    entities: {
      units: { all },
    },
  } = state;
  return {
    userOrgId: orgId,
    orgsById,
    isSuper: userAuthLevel === AUTH_LEVELS.SUPER_ADMIN,
    units: all
      .filter((u) => u.unit_type === 'http://qudt.org/vocab/quantitykind/Area')
      .filter((u) => u.name_abbr === 'ft2' || u.name_abbr === 'm2'),
    buildings: Object.values(byId),
  };
}

function mapDispatchToProps(dispatch) {
  return {
    createBuilding: (req) => dispatch(createBuildingActionCreator(req)),
    createDeployment: (site, name, orgId) =>
      dispatch(createDeploymentActionCreator(site, name, orgId)),
    linkDeployment: (site, bid) => dispatch(associateBuildingsAction(site, [bid])),
    updateDeploymentConfig: (site, port) =>
      dispatch(
        upsertDeploymentConfigAction(site, { port, network: '0.0.0.0/32', poll_freq_secs: 60 })
      ),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(NewBuilding);
